export interface PersistentUpload {
  id: string;
  scope: string;
  file: Blob;
  fileName: string;
  mimeType: string;
  eventId: string;
  albumId: string | null;
  caption: string;
  checksum?: string;
  sessionId?: string;
  parts: Array<{ partNumber: number; etag: string }>;
  status: 'queued' | 'uploading' | 'paused' | 'failed';
  progress: number;
  attempts: number;
  retryAt: number;
  leaseUntil: number;
  leaseOwner?: string;
  createdAt: number;
}

const databaseName = 'tov-plus-upload-queue-v1';
const storeName = 'uploads';

// WebKit can abort IndexedDB writes containing File/Blob values. Store bytes,
// then reconstruct a Blob at the API boundary; existing Blob records still work.
type StoredUpload = Omit<PersistentUpload, 'file'> & {
  file: Blob | ArrayBuffer;
};
function restoreFile(upload: StoredUpload): PersistentUpload {
  return {
    ...upload,
    file:
      upload.file instanceof ArrayBuffer
        ? new Blob([upload.file], { type: upload.mimeType })
        : upload.file,
  };
}
function storedSize(upload: StoredUpload) {
  return upload.file instanceof ArrayBuffer
    ? upload.file.byteLength
    : upload.file.size;
}

async function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(databaseName, 1);
    request.onupgradeneeded = () => {
      request.result
        .createObjectStore(storeName, { keyPath: 'id' })
        .createIndex('scope', 'scope');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () =>
      reject(new Error('Upload storage is blocked by another browser tab.'));
  });
}

async function transaction<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore, done: (value: T) => void) => void,
): Promise<T> {
  const db = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      let result: T;
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () =>
        reject(tx.error ?? new Error('Upload storage transaction aborted.'));
      run(tx.objectStore(storeName), (value) => {
        result = value;
      });
    });
  } finally {
    db.close();
  }
}

export async function listPersistentUploads(scope: string) {
  return transaction<PersistentUpload[]>('readonly', (store, done) => {
    const request = store.index('scope').getAll(scope);
    request.onsuccess = () =>
      done(
        (request.result as StoredUpload[])
          .map(restoreFile)
          .sort((a, b) => a.createdAt - b.createdAt),
      );
  });
}

export async function enqueuePersistentUploads(uploads: PersistentUpload[]) {
  if (!uploads.length) return;
  if (uploads.some((upload) => upload.scope !== uploads[0]!.scope))
    throw new Error('Uploads must share one identity scope.');
  const bytes = uploads.reduce((total, upload) => total + upload.file.size, 0);
  if (uploads.length > 100 || bytes > 250 * 1024 * 1024)
    throw new Error('Upload batch exceeds browser storage limits.');
  const estimate = await navigator.storage?.estimate?.().catch(() => undefined);
  if (estimate?.quota && estimate.quota - (estimate.usage ?? 0) < bytes * 1.1)
    throw new Error('There is not enough browser storage for these files.');
  // Read files before opening the transaction, which must not await file I/O.
  const stored = await Promise.all(
    uploads.map(async (upload) => ({
      ...upload,
      file: await upload.file.arrayBuffer(),
    })),
  );
  await transaction<void>('readwrite', (store, done) => {
    const existing = store.index('scope').getAll(uploads[0]!.scope);
    existing.onsuccess = () => {
      const current = existing.result as StoredUpload[];
      if (
        current.length + uploads.length > 100 ||
        current.reduce((total, upload) => total + storedSize(upload), bytes) >
          250 * 1024 * 1024
      ) {
        store.transaction.abort();
        return;
      }
      stored.forEach((upload) => store.add(upload));
      done(undefined);
    };
  });
  // Best effort; a refusal never turns a successfully saved queue into failure.
  await navigator.storage?.persist?.().catch(() => false);
}

export async function updatePersistentUpload(
  id: string,
  patch: Partial<PersistentUpload>,
  leaseOwner?: string,
) {
  const storedPatch = patch.file
    ? { ...patch, file: await patch.file.arrayBuffer() }
    : patch;
  return transaction<PersistentUpload | null>('readwrite', (store, done) => {
    const request = store.get(id);
    request.onsuccess = () => {
      if (!request.result) {
        done(null);
        return;
      }
      if (
        leaseOwner &&
        (request.result.leaseOwner !== leaseOwner ||
          request.result.status !== 'uploading')
      ) {
        done(null);
        return;
      }
      const next = { ...request.result, ...storedPatch, id } as StoredUpload;
      store.put(next);
      done(restoreFile(next));
    };
  });
}

export async function removePersistentUpload(id: string, leaseOwner?: string) {
  return transaction<void>('readwrite', (store, done) => {
    const request = store.get(id);
    request.onsuccess = () => {
      if (!leaseOwner || request.result?.leaseOwner === leaseOwner)
        store.delete(id);
      done(undefined);
    };
  });
}

/** Atomic claim prevents two tabs from transferring the same queued file. */
export async function claimPersistentUpload(scope: string, now = Date.now()) {
  return transaction<PersistentUpload | null>('readwrite', (store, done) => {
    const request = store.index('scope').getAll(scope);
    request.onsuccess = () => {
      const candidate = (request.result as StoredUpload[])
        .sort((a, b) => a.createdAt - b.createdAt)
        .find(
          (upload) =>
            (upload.status === 'queued' || upload.status === 'uploading') &&
            upload.retryAt <= now &&
            upload.leaseUntil <= now,
        );
      if (!candidate) {
        done(null);
        return;
      }
      const next = {
        ...candidate,
        status: 'uploading' as const,
        leaseUntil: now + 180_000,
        leaseOwner: crypto.randomUUID(),
      };
      store.put(next);
      done(restoreFile(next));
    };
  });
}

export async function uploadScope(occasionSlug: string, guestToken: string) {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(`${occasionSlug}\0${guestToken}`),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
}
